import React from 'react';
import { IconImage, IconImageSearch } from '@bynder/icons';
import styled from 'styled-components';
import { Spinner, token } from '@bynder/design-system';
import { Translate, useTranslate } from '@bynder/localization';
import { ImageAction } from '../../types';
import PlaceholderFile from '../shared/PlaceholderFile';
import { StyledMediaWidget } from '../shared/styled-components/Widgets';
import { UserType, useUserType } from '../../helpers/hooks';
import { API_ROUTES } from '../../constants/app';
import { EmptyWidget } from './EmptyWidget';
import {
  isLinkToUrl,
  isNoAction,
  isOpenPreview,
} from '../../constants/widgets';
import { useGuideStore } from '../../stores/guideStore';
import { useModalStore } from '../../stores/modalStore';
import { useNavigation } from '../../hooks/useNavigation';
import { useUploaderStore } from '../../stores/uploaderStore';
import { CSS_PROPERTIES } from '../guides/Inspector/constants';
import { useWidgetStyleProperty } from '../guides/Inspector/helpers';

export const MAX_ALLOWED_IMAGE_SIZE = 10485760;
export const MAX_ALLOWED_SIZE_IN_MB = '10';

interface Props {
  widgetId: string;
  readOnly: boolean;
  assetName?: string;
  imageUrl?: string;
  detailUrl?: string;
  assetId?: string;
  assetExtension?: string;
  numberOfColumns: number;
  imageAction?: ImageAction;
  widgetStyle: string;
}

const ImageWidget = ({
  widgetId,
  readOnly,
  assetName,
  imageUrl,
  detailUrl,
  assetId,
  assetExtension,
  numberOfColumns,
  imageAction,
  widgetStyle,
}: Props) => {
  const isEditMode = useGuideStore(s => s.isEditMode);
  const showAssetDetailOverlay = useModalStore.use.showAssetDetailOverlay();
  const { navigate } = useNavigation();
  const { translate } = useTranslate();
  const isEmpty = !assetId || !assetId.length;
  const hasPreview = !!(imageUrl && imageUrl.length);
  const imgRef = React.useRef<HTMLImageElement>();
  const [fullWidth, setFullWidth] = React.useState(false);
  const [error, setError] = React.useState(false);
  const [imageSrc, setImageSrc] = React.useState(imageUrl);
  const { type, url, openNewTab } = imageAction;
  const { userType } = useUserType();

  const { value: borderRadius } = useWidgetStyleProperty(
    widgetStyle,
    'widgets.media.borderRadius',
    CSS_PROPERTIES.BORDER_RADIUS,
  );

  const isUploading = useUploaderStore.use
    .uploadingImages()
    .some(image => image.id === widgetId);

  const hasImgNaturalWidthBiggerOrEqualThan = (width: number) =>
    imgRef.current && imgRef.current.naturalWidth >= width;

  const handleLoaded = React.useCallback(() => {
    if (numberOfColumns === 1) {
      setFullWidth(hasImgNaturalWidthBiggerOrEqualThan(800));
    } else {
      setFullWidth(hasImgNaturalWidthBiggerOrEqualThan(400));
    }
  }, [numberOfColumns]);

  const handleImageError = React.useCallback(() => setError(true), [setError]);

  React.useEffect(() => {
    handleLoaded();
  }, [handleLoaded, numberOfColumns]);

  React.useEffect(() => {
    setImageSrc(imageUrl);
  }, [setImageSrc, imageUrl]);

  if (readOnly && isEmpty) {
    return null;
  }

  const img = (
    <StyledImage
      data-testid="image-widget-image"
      assetId={assetId}
      ref={imgRef}
      src={imageSrc}
      alt={assetName}
      onLoad={handleLoaded}
      onError={handleImageError}
      $fullWidth={fullWidth || assetExtension === 'svg'}
      $borderRadius={`${borderRadius}px`}
    />
  );

  const isLoggedIn =
    userType === UserType.LOGGED_IN || userType === UserType.OPEN_ASSET_BANK;

  const handleClick = () => {
    if (isOpenPreview(type)) {
      if (isLoggedIn) {
        window.open(API_ROUTES.ASSET_OPEN(assetId), '_blank');
      } else {
        showAssetDetailOverlay(assetName, detailUrl, imageUrl, false);
      }
    } else if (isLinkToUrl(type)) {
      let urlWithHttps = url;

      if (!/^(http:|https:|mailto:)/i.test(urlWithHttps)) {
        urlWithHttps = 'https://' + urlWithHttps;
      }
      navigate(urlWithHttps, openNewTab);
    }
  };

  const renderImageOrPlaceholder = () => {
    if (!isEmpty) {
      if (!hasPreview) {
        return (
          <PlaceholderFile assetExtension={assetExtension} title={assetName} />
        );
      }

      return img;
    }
  };

  const renderClickableImage = (onClick?: () => void) => {
    return (
      <StyledClickableImage onClick={onClick || handleClick}>
        {renderImageOrPlaceholder()}
      </StyledClickableImage>
    );
  };

  const renderReadOnlyImage = () => {
    if (isNoAction(type)) {
      return renderImageOrPlaceholder();
    }

    return renderClickableImage();
  };

  const renderEditableImage = () => {
    if (!isEmpty) {
      return renderImageOrPlaceholder();
    }

    return (
      <>
        <StyledImageWidgetPickerContainer>
          <IconImageSearch />
          <Translate id="IMAGE_WIDGET.EMPTY_BUTTON_TEXT" />
        </StyledImageWidgetPickerContainer>
      </>
    );
  };

  if (isUploading) {
    return <EmptyWidget icon={<Spinner />} text={'Uploading Image...'} />;
  }

  if (isEmpty && isEditMode) {
    return (
      <EmptyWidget
        icon={<IconImage />}
        text={translate('WIDGETS_CONSTANTS.EMPTY_IMAGE_WIDGET')}
      />
    );
  }

  return (
    <StyledMediaWidget
      data-testid="image-widget"
      $isEmpty={isEmpty}
      $hasPreview={!error && hasPreview}
      $extension={`.${assetExtension}`}
    >
      {readOnly ? renderReadOnlyImage() : renderEditableImage()}
    </StyledMediaWidget>
  );
};

const StyledImage = styled.img<{
  $fullWidth: boolean;
  $borderRadius: string;
  assetId?: string;
}>`
  width: ${props => (props.$fullWidth ? '100%' : 'auto')};
  border-radius: ${props => props.$borderRadius};
`;

const StyledClickableImage = styled.div`
  width: 100%;
  cursor: pointer;
  border-radius: ${token.radiusLarge};
`;

const StyledImageWidgetPickerContainer = styled.div`
  height: 100%;
  width: 100%;
  align-items: center;
  justify-content: center;
  gap: 12px;
  display: flex;
  color: ${token.gray600a};
  :hover {
    color: ${token.gray800a};
  }
`;

export default ImageWidget;
